/**
 * Variant space for the in-session AI capability-signalling experiment.
 *
 * Five arms at 20% each. Entry-point form and placement are held constant as
 * the shipped in-canvas composer throughout — placement belongs to the
 * separate prominence stream, and varying form alongside the payload would
 * leave a losing arm undiagnosable.
 *
 * Ids are semantic rather than the doc's A–E letters so an analysis cut reads
 * without a legend; the letter each one carries is in the registry label.
 *
 * Kept out of `capability-signalling-flag.ts` so the arm space is importable
 * without pulling in OpenFeature and React — the split `diarisation-variant.ts`
 * uses for the same reason.
 */
export const CAPABILITY_SIGNALLING_VARIANTS = [
  'control',
  'dynamic_idle',
  'reused_starters',
  'recut_starters',
  'scoped_context',
] as const;

export type CapabilitySignallingVariant = (typeof CAPABILITY_SIGNALLING_VARIANTS)[number];

export const CAPABILITY_SIGNALLING_FLAG_KEY =
  'exp-evidence-in-session-capability-signalling';

export const CAPABILITY_SIGNALLING_DEFAULT: CapabilitySignallingVariant = 'control';

/**
 * Narrows a raw flag string to an arm, falling back to control.
 *
 * The fallback is the point: an unresolved provider, a typo in Flagsmith or a
 * variant retired mid-experiment all have to land on today's shipped behaviour
 * rather than an arm nobody is measuring.
 */
export const parseCapabilitySignallingVariant = (
  raw: string | null | undefined
): CapabilitySignallingVariant => {
  const candidate = raw?.trim();
  return CAPABILITY_SIGNALLING_VARIANTS.includes(candidate as CapabilitySignallingVariant)
    ? (candidate as CapabilitySignallingVariant)
    : CAPABILITY_SIGNALLING_DEFAULT;
};
